/**
 * Pointer-drag maths (Phase 151).
 *
 * The DOM-free half of `usePointerDrag`. It lives apart from the hook for the
 * usual reason: "where does this land" is the part that is easy to get subtly
 * wrong, impossible to notice by looking at the screen, and trivial to assert.
 *
 * Pointer events rather than HTML5 drag-and-drop. The native API cannot be
 * styled (the drag image is a browser artefact), fires no events on touch at
 * all, and ends the drag if the source element unmounts. Pointer events are one
 * code path for mouse, pen and touch, and the "lifted" card is just a div we
 * position ourselves.
 */

/**
 * How far the pointer must travel before a press becomes a drag.
 *
 * Zero would mean every click on a card starts a drag and every drag ends as a
 * click somewhere. Eight pixels is the distance a hand resting on a trackpad
 * moves during a deliberate click, and comfortably under the distance a
 * deliberate drag covers before anyone expects feedback.
 */
export const DRAG_THRESHOLD_PX = 8;

/**
 * How long a touch must be held before it becomes a drag.
 *
 * Touch has no hover, so a finger moving across a scrollable column is
 * ambiguous: it could be a scroll or a drag. A short hold disambiguates it the
 * way every touch OS does. Mouse and pen skip this entirely — they have a
 * separate scroll gesture.
 */
export const TOUCH_HOLD_MS = 220;

export interface Point {
  x: number;
  y: number;
}

/** Has the pointer moved far enough from where it went down to mean a drag? */
export function passedThreshold(from: Point, to: Point, threshold = DRAG_THRESHOLD_PX): boolean {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return dx * dx + dy * dy >= threshold * threshold;
}

/** The subset of `DOMRect` this module needs, so tests need no layout engine. */
export interface Span {
  start: number;
  size: number;
}

/**
 * Which slot does a pointer at `position` want, given the items already there?
 *
 * `spans` are the item boxes along the drag axis, in render order. The answer is
 * an insertion index: 0 is before everything, `spans.length` is after everything.
 * Splitting on each item's midpoint rather than on its edge is what makes the
 * insertion line feel like it tracks the pointer instead of lagging one item
 * behind it.
 *
 * `skip` is the index of the item being dragged, which is still rendered (see
 * `afterCardId` in workflow-board.ts for why it stays mounted). It is not a
 * landmark for its own landing spot, so it is passed over.
 */
export function insertionIndex(
  spans: readonly Span[],
  position: number,
  skip: number | null = null,
): number {
  let index = spans.length;
  for (const [at, span] of spans.entries()) {
    if (at === skip) continue;
    if (position < span.start + span.size / 2) {
      index = at;
      break;
    }
  }
  return index;
}

/**
 * Would dropping at `index` leave the dragged item exactly where it already is?
 *
 * True for the slot the item occupies AND the one immediately after it — both
 * describe the same resting place, and drawing an insertion line in either would
 * promise a change that dropping does not deliver.
 */
export function isSelfSlot(index: number, fromIndex: number): boolean {
  return fromIndex >= 0 && (index === fromIndex || index === fromIndex + 1);
}

/**
 * Keep a dragged element's preview inside the viewport.
 *
 * Without this, dragging a card towards the top of the screen lets the preview
 * ride up under the browser chrome, and the last thing the user sees before
 * dropping is half a card.
 */
export function clampToViewport(
  left: number,
  top: number,
  width: number,
  height: number,
  viewport: { width: number; height: number },
): Point {
  return {
    x: Math.min(Math.max(left, 0), Math.max(viewport.width - width, 0)),
    y: Math.min(Math.max(top, 0), Math.max(viewport.height - height, 0)),
  };
}

/**
 * How fast should a container scroll while a drag hovers near its edge?
 *
 * Returns pixels per frame — negative towards the start, positive towards the
 * end, zero in the calm middle. Without edge-scrolling, a board wider than the
 * window can only be dragged across in stages: pick up, drop, scroll, pick up
 * again.
 */
export function edgeScrollDelta(
  position: number,
  bounds: { start: number; end: number },
  zone = 72,
  maxSpeed = 18,
): number {
  const fromStart = position - bounds.start;
  const fromEnd = bounds.end - position;
  if (fromStart < zone) {
    return -Math.round(maxSpeed * Math.min(1, (zone - Math.max(fromStart, 0)) / zone));
  }
  if (fromEnd < zone) {
    return Math.round(maxSpeed * Math.min(1, (zone - Math.max(fromEnd, 0)) / zone));
  }
  return 0;
}
